import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import { useSidebar } from '@/hooks/useSidebar';
import { cn } from '@/lib/utils';
import { timeFromNow } from '@/util/dateUtils';

import { EmptyImprovements } from './EmptyImprovements';
import type { Improvement, ImprovementStatus } from '../types';
import {
  FILTERABLE_STATUSES,
  SEVERITY_BADGE_FLAVOUR,
  SEVERITY_TRANSLATION_KEYS,
  STATUS_BADGE_FLAVOUR,
  STATUS_TRANSLATION_KEYS,
} from '../types';
import { getEmptyState, humanizeKey } from '../utils';

interface ImprovementsListProps {
  brainId: string | undefined;
  improvements: Improvement[];
  /** Whether the brain has any improvements at all (before status filtering). */
  hasAny: boolean;
  isLoading: boolean;
  isError: boolean;
  /** Per-brain toggle state — selects the waiting empty-state copy. */
  improvementsEnabled: boolean;
  statusFilter: ImprovementStatus | 'all';
  onStatusFilterChange: (status: ImprovementStatus | 'all') => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

const ListSkeleton = () => (
  <div className="divide-y divide-stroke rounded-lg border border-stroke bg-background">
    {Array.from({ length: 4 }, (_, i) => (
      <div key={i} className="flex flex-col gap-2 px-4 py-3">
        <Skeleton className="h-4 w-2/5" />
        <Skeleton className="h-4 w-4/5" />
      </div>
    ))}
  </div>
);

export const ImprovementsList = ({
  brainId,
  improvements,
  hasAny,
  isLoading,
  isError,
  improvementsEnabled,
  statusFilter,
  onStatusFilterChange,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: ImprovementsListProps) => {
  const { t } = useTranslation();
  const { open, meta, isOpen } = useSidebar();
  const { showEmptySkeleton, showList } = useEmptyStateView({
    scope: `improvements:${brainId}`,
    isLoading,
    isEmpty: !hasAny,
    isReady: !!brainId,
    isError,
  });

  if (showEmptySkeleton) {
    return <EmptySkeleton media="icon" descriptionLines={2} actions={0} />;
  }

  if (isLoading) {
    return showList ? <ListSkeleton /> : null;
  }

  const emptyState = getEmptyState(hasAny, improvements.length);

  // No improvements at all: full waiting hero, no filter toolbar (D8).
  if (emptyState === 'waiting') {
    return <EmptyImprovements variant="waiting" improvementsEnabled={improvementsEnabled} />;
  }

  return (
    <div className="flex min-h-0 flex-col gap-4">
      <div className="flex items-center">
        <Select
          value={statusFilter}
          onValueChange={(value) => onStatusFilterChange(value as ImprovementStatus | 'all')}
        >
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FILTERABLE_STATUSES.map((status) => (
              <SelectItem key={status} value={status}>
                {t(STATUS_TRANSLATION_KEYS[status])}
              </SelectItem>
            ))}
            <SelectItem value="all">{t('improvements.all_statuses')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {emptyState === 'caught_up' ? (
        <EmptyImprovements variant="caught_up" />
      ) : (
        <div className="min-h-0 overflow-y-auto">
          <div className="divide-y divide-stroke rounded-lg border border-stroke bg-background">
            {improvements.map((improvement) => {
              const isSelected =
                isOpen('improvement_details') && meta?.improvementId === improvement.improvement_id;

              return (
                <button
                  key={improvement.improvement_id}
                  type="button"
                  onClick={() =>
                    open('improvement_details', { improvementId: improvement.improvement_id })
                  }
                  className={cn(
                    'flex w-full flex-col gap-1.5 px-4 py-3 text-left first:rounded-t-lg last:rounded-b-lg hover:bg-bg-muted-50',
                    isSelected && 'bg-bg-muted-50'
                  )}
                >
                  <div className="flex w-full items-center gap-2">
                    <Badge type="secondary" flavour={SEVERITY_BADGE_FLAVOUR[improvement.severity]}>
                      {t(SEVERITY_TRANSLATION_KEYS[improvement.severity])}
                    </Badge>
                    <span className="min-w-0 truncate text-sm font-semibold text-foreground">
                      {improvement.title}
                    </span>
                    {statusFilter === 'all' && (
                      <Badge type="secondary" flavour={STATUS_BADGE_FLAVOUR[improvement.status]}>
                        {t(STATUS_TRANSLATION_KEYS[improvement.status])}
                      </Badge>
                    )}
                    <span className="ml-auto shrink-0 text-xs text-foreground-muted">
                      {timeFromNow(improvement.created)}
                    </span>
                  </div>
                  <div className="flex w-full items-center gap-2">
                    <Badge type="outline" flavour="neutral">
                      {humanizeKey(improvement.improvement_type)}
                    </Badge>
                    <span className="min-w-0 truncate text-sm text-foreground-muted">
                      {improvement.summary}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {hasNextPage && (
            <div className="flex justify-center py-4">
              <Button
                variant="outline-neutral"
                size="sm"
                isLoading={isFetchingNextPage}
                onClick={onLoadMore}
              >
                {t('improvements.load_more')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
